/**
 * Loading Component
 * 
 * Displays loading states with multiple animation types
 * - Spinner: Rotating circle for active operations
 * - Pulse: Fading element for data processing
 * - Skeleton: Shimmer effect for content placeholders
 * 
 * Supports inline and full-page loading overlays with theme support
 * 
 * @component
 * @example
 * // Inline spinner while fetching data
 * <Loading message="Carregando dados..." />
 * 
 * @example
 * // Full-page loading overlay
 * <Loading fullPage message="Processando..." />
 * 
 * @example
 * // Pulse animation with backdrop
 * <Loading type="pulse" size="lg" fullPage backdrop message="Salvando..." />
 * 
 * @example
 * // Skeleton loading in a card
 * <Loading type="skeleton" size="md" />
 */

import { FC } from 'react';
import clsx from 'clsx';
import type { LoadingProps } from './Loading.types';
import styles from './Loading.module.css';

const Loading: FC<LoadingProps> = ({
  size = 'md',
  type = 'spinner',
  message,
  fullPage = false,
  backdrop = true,
  className,
  ariaLabel = 'Carregando...',
}) => {
  // Render spinner animation
  const renderSpinner = () => (
    <div
      className={clsx(styles.spinner, styles[`spinner${size.charAt(0).toUpperCase() + size.slice(1)}`])}
      aria-hidden="true"
      role="status"
    />
  );

  // Render pulse animation
  const renderPulse = () => (
    <div
      className={clsx(styles.pulse, styles[`pulse${size.charAt(0).toUpperCase() + size.slice(1)}`])}
      aria-hidden="true"
      role="status"
    />
  );

  // Render skeleton animation
  const renderSkeleton = () => (
    <div
      className={clsx(styles.skeleton, styles[`skeleton${size.charAt(0).toUpperCase() + size.slice(1)}`])}
      aria-hidden="true"
      role="status"
    />
  );

  // Select animation type
  const renderAnimation = () => {
    switch (type) {
      case 'pulse':
        return renderPulse();
      case 'skeleton':
        return renderSkeleton();
      case 'spinner':
      default:
        return renderSpinner();
    }
  };

  // Container classes
  const containerClasses = clsx(
    styles.loading,
    {
      [styles.loadingInline]: !fullPage,
      [styles.loadingFullPage]: fullPage,
      [styles.loadingFullPageWithBackdrop]: fullPage && backdrop,
    },
    className
  );

  return (
    <div className={containerClasses} role="status" aria-label={ariaLabel}>
      {renderAnimation()}
      {message && (
        <p className={styles.message} aria-live="polite">
          {message}
        </p>
      )}
      <span className="visually-hidden">{ariaLabel}</span>
    </div>
  );
};

Loading.displayName = 'Loading';

export default Loading;
